// The rules Healthlink enforces on a laboratory order, checked here before the
// order is submitted rather than after.
//
// The backend validates the same rules and remains the source of truth — but it can
// only answer once the GP has picked a destination and pressed send, and a
// meaningful share of real patients fail on data that has nothing to do with this
// order (an address line over 30 characters, a gender recorded as "Unspecified").
// Telling the GP up front which detail to fix is the difference between a fixable
// problem and a dead end.
//
// Mirrors OmlTemplateFieldValidator in api-practice. Two deliberate details:
//   * addressLine3 is NOT checked — OmlOrderBuilder maps only addressLine1,
//     addressLine2, city and county to XAD.1–XAD.4, so line 3 never goes on the
//     wire and its length cannot break anything.
//   * an unrecorded or non-binary gender fails rather than being coerced. Sex drives
//     laboratory reference ranges, so guessing it is the harmful option.

const NAME_MAX_LENGTH = 50;
const ADDRESS_LINE_MAX_LENGTH = 30;
const EARLIEST_DATE_OF_BIRTH = "1900-01-01";

const ACCEPTED_GENDERS = ["f", "m", "female", "male"];

const isBlank = (value) => !value || !String(value).trim();

const exceeds = (value, maxLength) => String(value ?? "").trim().length > maxLength;

const parseDate = (value) => {
  if (!value) return null;

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const validateName = (patient) => {
  const violations = [];

  if (isBlank(patient.surname)) {
    violations.push({
      field: "surname",
      message: "The patient's surname is missing.",
    });
  } else if (exceeds(patient.surname, NAME_MAX_LENGTH)) {
    violations.push({
      field: "surname",
      message: `The patient's surname is longer than ${NAME_MAX_LENGTH} characters, which a laboratory order can't carry.`,
    });
  }

  if (isBlank(patient.firstName)) {
    violations.push({
      field: "firstName",
      message: "The patient's first name is missing.",
    });
  } else if (exceeds(patient.firstName, NAME_MAX_LENGTH)) {
    violations.push({
      field: "firstName",
      message: `The patient's first name is longer than ${NAME_MAX_LENGTH} characters, which a laboratory order can't carry.`,
    });
  }

  return violations;
};

const validateDateOfBirth = (patient) => {
  if (isBlank(patient.dateOfBirth)) {
    return [
      {
        field: "dateOfBirth",
        message: "The patient's date of birth is missing.",
      },
    ];
  }

  const dateOfBirth = parseDate(patient.dateOfBirth);

  if (!dateOfBirth) {
    return [
      {
        field: "dateOfBirth",
        message: "The patient's date of birth isn't a valid date.",
      },
    ];
  }

  if (dateOfBirth < new Date(EARLIEST_DATE_OF_BIRTH)) {
    return [
      {
        field: "dateOfBirth",
        message: "The patient's date of birth is before 1900 — please correct it.",
      },
    ];
  }

  if (dateOfBirth > new Date()) {
    return [
      {
        field: "dateOfBirth",
        message: "The patient's date of birth is in the future — please correct it.",
      },
    ];
  }

  return [];
};

const validateGender = (patient) => {
  const gender = String(patient.gender ?? "")
    .trim()
    .toLowerCase();

  if (ACCEPTED_GENDERS.includes(gender)) return [];

  return [
    {
      field: "gender",
      message: isBlank(patient.gender)
        ? "The patient has no recorded gender. A laboratory order can only be sent for a patient recorded as male or female."
        : `The patient's gender is recorded as “${patient.gender}”. A laboratory order can only be sent for a patient recorded as male or female.`,
    },
  ];
};

const ADDRESS_LINES = [
  { key: "addressLine1", label: "Address line 1" },
  { key: "addressLine2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
];

const validateAddress = (patient) => {
  const address = patient.address ?? {};

  if (isBlank(address.addressLine1)) {
    return [
      {
        field: "addressLine1",
        message: "The patient's address is missing its first line.",
      },
    ];
  }

  const violations = ADDRESS_LINES.filter(({ key }) =>
    exceeds(address[key], ADDRESS_LINE_MAX_LENGTH)
  ).map(({ key, label }) => ({
    field: key,
    message: `${label} is longer than ${ADDRESS_LINE_MAX_LENGTH} characters, which a laboratory order can't carry.`,
  }));

  if (exceeds(address.postCode, ADDRESS_LINE_MAX_LENGTH)) {
    violations.push({
      field: "postCode",
      message: `The Eircode is longer than ${ADDRESS_LINE_MAX_LENGTH} characters, which a laboratory order can't carry.`,
    });
  }

  return violations;
};

// Returns every violation in one pass, so the GP fixes the record once instead of
// discovering the next problem on the next attempt. An absent patient returns no
// violations: nothing is known yet, and blocking on "still loading" would be wrong.
export const getLabOrderPreflightViolations = (patient) => {
  if (!patient) return [];

  return [
    ...validateName(patient),
    ...validateDateOfBirth(patient),
    ...validateGender(patient),
    ...validateAddress(patient),
  ];
};

export const canPlaceLabOrder = (patient) =>
  !!patient && getLabOrderPreflightViolations(patient).length === 0;

export {
  ADDRESS_LINE_MAX_LENGTH,
  EARLIEST_DATE_OF_BIRTH,
  NAME_MAX_LENGTH,
};
